/**
 * Setting one window.
 *
 * The whole design problem is in one sentence: "opens on the 3rd" does not say the 3rd of
 * *which* month. Almost always it is the month after the period, which is why nobody says
 * it — and the project that opens on the 28th of the month *before* is precisely the case
 * that the unspoken convention cannot express, and precisely what the client asked for.
 *
 * So each edge is two controls, a day and a month, and underneath them the screen shows the
 * dates they resolve to for a real period. Somebody who picks the wrong month sees it
 * immediately rather than a month later when nobody could enter anything.
 */
import { useMutation, useQuery } from '@tanstack/react-query'
import { type Ref, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api, ApiError } from '@/api/client'
import { Icon } from '@/components/Icon'
import type { ScheduleRow } from '@/features/periods/PeriodSchedule'

type ProjectOption = { id: string; title: string; dof_project_number: string }

/** Mirrors `MIN_MONTH_OFFSET`/`MAX_MONTH_OFFSET` on the API, which is the authority. */
const OFFSETS = [-2, -1, 0, 1, 2] as const

const DAYS = Array.from({ length: 31 }, (_, index) => index + 1)

/** The period the preview resolves against — a 31-day month, so clamping is visible. */
const EXAMPLE = '2026-08'

export function ScheduleEditor({
  row,
  taken,
  onClose,
  onSaved,
}: {
  row: ScheduleRow | null
  taken: string[]
  onClose: () => void
  onSaved: () => void
}) {
  const { t, i18n } = useTranslation()
  const first = useRef<HTMLSelectElement | null>(null)

  // `row` with a null project is the tenant default; `row` with one is an existing
  // exception; `null` is a new exception being added.
  const isDefault = row !== null && row.project_id === null
  const existing = row?.project_id ?? null

  const [projectId, setProjectId] = useState(existing ?? '')
  const [opensDay, setOpensDay] = useState(row?.opens.day ?? 3)
  const [opensOffset, setOpensOffset] = useState(row?.opens.month_offset ?? 1)
  const [closesDay, setClosesDay] = useState(row?.closes.day ?? 10)
  const [closesOffset, setClosesOffset] = useState(row?.closes.month_offset ?? 1)
  const [note, setNote] = useState(row?.note ?? '')
  const [error, setError] = useState<string | null>(null)

  const projects = useQuery({
    queryKey: ['projects', 'all'],
    queryFn: ({ signal }) =>
      api.get<{ items: ProjectOption[] }>('/projects?limit=200', signal),
    enabled: !isDefault && existing === null,
  })

  useEffect(() => {
    first.current?.focus()
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', escape)
    return () => document.removeEventListener('keydown', escape)
  }, [onClose])

  // The preview, computed here rather than fetched. A round trip per keystroke to see what
  // a date resolves to would make the control feel broken, and this arithmetic is small.
  const preview = useMemo(
    () =>
      resolve(EXAMPLE, opensDay, opensOffset, closesDay, closesOffset, i18n.language),
    [opensDay, opensOffset, closesDay, closesOffset, i18n.language],
  )

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        opens: { day: opensDay, month_offset: opensOffset },
        closes: { day: closesDay, month_offset: closesOffset },
        note: note.trim() || null,
      }
      const path = isDefault ? '/period-schedule/default' : `/period-schedule/${projectId}`
      return api.put(path, body)
    },
    onSuccess: onSaved,
    onError: (caught) => setError(caught instanceof ApiError ? caught.friendly : String(caught)),
  })

  const incomplete =
    (!isDefault && !projectId) ||
    preview.empty ||
    (existing === null && !isDefault && taken.includes(projectId))

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div
        className="modal modal--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-editor-title"
        data-testid="schedule-editor"
      >
        <header className="modal__head modal__head--plain">
          <Icon name="periods" size={16} />
          <h2 className="modal__title" id="schedule-editor-title">
            {isDefault ? t('schedule.editDefault') : t('schedule.editProject')}
          </h2>
        </header>

        <div className="modal__body" data-scroll="1">
          {!isDefault ? (
            <label className="field field--wide">
              <span className="field__label">{t('schedule.project')}</span>
              {existing ? (
                <input
                  className="field__input"
                  value={row?.project_title ?? ''}
                  disabled
                  data-testid="schedule-project-fixed"
                />
              ) : (
                <select
                  className="field__input"
                  value={projectId}
                  onChange={(event) => setProjectId(event.target.value)}
                  data-testid="schedule-project"
                >
                  <option value="">{t('schedule.chooseProject')}</option>
                  {(projects.data?.items ?? [])
                    .filter((project) => !taken.includes(project.id))
                    .map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.dof_project_number} · {project.title}
                      </option>
                    ))}
                </select>
              )}
            </label>
          ) : null}

          <Edge
            legend={t('schedule.opens')}
            day={opensDay}
            offset={opensOffset}
            onDay={setOpensDay}
            onOffset={setOpensOffset}
            testid="opens"
            dayRef={first}
          />

          <Edge
            legend={t('schedule.closes')}
            day={closesDay}
            offset={closesOffset}
            onDay={setClosesDay}
            onOffset={setClosesOffset}
            testid="closes"
          />

          {/* What the two rules actually mean, for a real month. Nobody should have to do
              this arithmetic in their head to check they configured what they meant. */}
          <div
            className={`schedule__preview${preview.empty ? ' schedule__preview--bad' : ''}`}
            data-testid="schedule-preview"
          >
            <Icon name={preview.empty ? 'warning' : 'clock'} size={15} />
            <span>
              {preview.empty
                ? t('schedule.neverOpen')
                : t('schedule.previewText', {
                    period: EXAMPLE,
                    opens: preview.opens,
                    closes: preview.closes,
                    days: preview.days,
                  })}
            </span>
          </div>

          <label className="field field--wide">
            <span className="field__label">{t('schedule.why')}</span>
            <input
              className="field__input"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={t('schedule.whyHint')}
              data-testid="schedule-note"
            />
            <span className="field__hint">{t('schedule.whyHint')}</span>
          </label>

          {error ? (
            <p className="modal__error" role="alert" data-testid="schedule-editor-error">
              {error}
            </p>
          ) : null}
        </div>

        <footer className="modal__foot">
          <button type="button" className="button button--quiet" onClick={onClose}>
            {t('edit.cancel')}
          </button>
          <button
            type="button"
            className="button button--primary button--wide"
            disabled={incomplete || save.isPending}
            onClick={() => save.mutate()}
            data-testid="schedule-save"
          >
            {save.isPending ? t('builder.saving') : t('schedule.save')}
          </button>
        </footer>
      </div>
    </>
  )
}

function Edge({
  legend,
  day,
  offset,
  onDay,
  onOffset,
  testid,
  dayRef,
}: {
  legend: string
  day: number
  offset: number
  onDay: (value: number) => void
  onOffset: (value: number) => void
  testid: string
  dayRef?: Ref<HTMLSelectElement>
}) {
  const { t } = useTranslation()
  return (
    <fieldset className="schedule__edge">
      <legend className="field__label">{legend}</legend>
      <div className="schedule__edgeControls">
        <label className="field">
          <span className="field__label">{t('schedule.day')}</span>
          <select
            ref={dayRef}
            className="field__input"
            value={day}
            onChange={(event) => onDay(Number(event.target.value))}
            data-testid={`schedule-${testid}-day`}
          >
            {DAYS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
          {/* The 31st is the last day of a short month, not the 1st of the next one. */}
          {day > 28 ? (
            <span className="field__hint">{t('schedule.shortMonth')}</span>
          ) : null}
        </label>

        <label className="field">
          <span className="field__label">{t('schedule.ofMonth')}</span>
          <select
            className="field__input"
            value={offset}
            onChange={(event) => onOffset(Number(event.target.value))}
            data-testid={`schedule-${testid}-offset`}
          >
            {OFFSETS.map((value) => (
              <option key={value} value={value}>
                {t(`schedule.offset.${value}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
    </fieldset>
  )
}

/** The same arithmetic the API does, for the live preview. Kept small and total. */
function resolve(
  period: string,
  opensDay: number,
  opensOffset: number,
  closesDay: number,
  closesOffset: number,
  locale: string,
): { opens: string; closes: string; days: number; empty: boolean } {
  const [year, month] = period.split('-').map(Number) as [number, number]
  const edge = (day: number, offset: number) => {
    const index = year * 12 + (month - 1) + offset
    const y = Math.floor(index / 12)
    const m = (index % 12) + 1
    // Day 0 of the next month is the last day of this one.
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
    return new Date(Date.UTC(y, m - 1, Math.min(day, last)))
  }
  const opens = edge(opensDay, opensOffset)
  const closes = edge(closesDay, closesOffset)
  const format = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
  return {
    opens: format.format(opens),
    closes: format.format(closes),
    // Inclusive of both ends, matching `PeriodWindow.contains`.
    days: Math.round((closes.getTime() - opens.getTime()) / 86_400_000) + 1,
    empty: closes < opens,
  }
}
